import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import PropTypes from 'prop-types';
import { Stack, Typography, Box, TextField, Dialog, DialogContent, Autocomplete, Checkbox, CircularProgress } from '@mui/material';
import axios from '../../utils/axios';
import { objectToFormData } from '../../utils/objectToFormData';

ParentForm.propTypes = {
  netTerms: PropTypes.array,
  parentInEdit: PropTypes.object,
  setParents: PropTypes.func,
  open: PropTypes.bool,
  close: PropTypes.func
}

export default function ParentForm({ netTerms, parentInEdit, setParents, open, close }) {
  const { enqueueSnackbar } = useSnackbar();
  const [submitting, setSubmitting] = useState(false);
  const [formValue, setFormValue] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && parentInEdit) { 
      setLoading(true);
      setFormValue({});
      axios.get(`/parent/parent?id=${parentInEdit.id}`)
        .then(res => {setFormValue(res.data); })
        .catch(() => {enqueueSnackbar('Get parent data failed', { variant: 'error' }); })
        .finally(() => setLoading(false));
    } else {
      setFormValue({ netTermId: 2 });
    }
  }, [open, parentInEdit]);

  const onSubmit = () => {
    setSubmitting(true);
    axios.post('/parent/parentForm', objectToFormData(formValue))
      .then((res) => {
        if (res.data) {
          setParents(currentParents => {
            if (parentInEdit) return currentParents.map(parent => parent.id === res.data.id ? res.data : parent);
            return [...currentParents, res.data];
          });
          close();
          enqueueSnackbar(`Parent ${(parentInEdit ? 'updated' : 'created')}`, { variant: 'success' });
        } })
      .catch(() => enqueueSnackbar(`${(parentInEdit ? 'Update' : 'Create')} parent failed`, { variant: 'error' }))
      .finally(() => setSubmitting(false));
  };

  return <Dialog open={open} onClose={close} maxWidth="md" >
    <DialogContent>
      {!loading ? <Stack spacing={1} minWidth="600px">
        <Box>
          <Typography variant="subtitle1">Parent Name*</Typography>
          <TextField
            name="name"
            size="small"
            placeholder="Enter Parent Name"
            fullWidth
            value={formValue.name ?? ''}
            onChange={(e) => setFormValue(form => ({ ...form, name: e.target.value }))}
          />
        </Box>
        <Box>
          <Typography variant="subtitle1">Net Terms*</Typography>
          <Autocomplete fullWidth size="small"
            value={netTerms.find(netTerm => netTerm.value === formValue.netTermId)}
            onChange={(event, val) => setFormValue(form => ({ ...form, netTermId: val?.value }))}
            options={netTerms}
            renderInput={(params) => <TextField {...params} placeholder="Net Terms" />}
          />
        </Box>
        <Box>
          <Checkbox checked={formValue.billFacilitiesToPurchasingGroupAddress ?? false}
            onChange={(e) => setFormValue(value => ({ ...value, billFacilitiesToPurchasingGroupAddress: e.target.checked }))} />
          Bill facilities under this parent to the purchasing group address
        </Box>
        <Box>
          <Checkbox checked={formValue.billFacilityForTariffSurcharge ?? false}
            onChange={(e) => setFormValue(value => ({ ...value, billFacilityForTariffSurcharge: e.target.checked }))} />
          Bill facilities under this parent for tariffs
        </Box>
        <Box>
          <Checkbox checked={formValue.noShippingChargeForInvoices ?? false}
            onChange={(e) => setFormValue(value => ({ ...value, noShippingChargeForInvoices: e.target.checked }))} />
          No shipping charge for invoices
        </Box>
        <Box>
          <LoadingButton loading={submitting} disabled={!formValue?.name?.trim() || !formValue?.netTermId} sx={{ display: 'flex', justifySelf: 'center', mb: 1, mt: 1, px: 7 }}
            color="purple" size="medium" variant="contained" onClick={onSubmit}>
            Save
          </LoadingButton>
        </Box>
      </Stack> : <Box sx={{ display: "flex", justifyContent: "center", m: 3 }}><CircularProgress /></Box>}
    </DialogContent>
  </Dialog>;
}